import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { ListGridAsyncData } from '../../shared/listGrid/listGrid.component';
import { CharterDAO } from '../../../core/services/dao/charterDAO';
import { VesselDAO } from '../../../core/services/dao/vesselDAO';
import { debounceTime } from 'rxjs/operators';
import { unsubscribeFromAll } from '../../../core/pure-functions/rx-helper';
import moment from 'moment';
import { ManageButtonComponent } from './manage-button/manage-button.component';
import { PayoutDAO } from '../../../core/services/dao/payoutDAO';
import { CharterStatus } from 'anchor-shared/lib/model/enum/charter-status';
import { getTypeofCharter } from 'anchor-shared/lib/service/utils/charter-helper';

@Component({
  selector: 'app-bookings-manager',
  templateUrl: './bookings-manager.component.html',
  styleUrls: ['./bookings-manager.component.scss'],
})
export class BookingsManagerComponent implements OnInit {
  filteredCharters: CharterSection[];
  payouts;
  ownerBoats: { label: string; value: number }[] = [
    {
      label: 'Filter by boat',
      value: null,
    },
  ];
  columnDefs: any[];
  charterBoats = [];
  boatFilter: number = null;
  searchFilter: string = null;
  charterStatus: Record<
    string,
    { label: (charter: any) => string; statusClass?: string }
  > = {
    [CharterStatus.needsApproval]: {
      label: () => 'Needs Approval',
      statusClass: 'text-danger',
    },
    [CharterStatus.pendingOffers]: {
      label: () => 'Pending Offers',
      statusClass: 'text-warning',
    },
    [CharterStatus.approvedPending]: {
      label: () => 'Approved—pending',
      statusClass: 'text-warning',
    },
    [CharterStatus.booked]: {
      label: () => 'Booked',
      statusClass: 'text-success',
    },
    [CharterStatus.completed]: {
      label: (charter) =>
        charter.payouts.findIndex((x) => x.paid) > -1
          ? 'Paid'
          : 'Pending Payout',
    },
    [CharterStatus.cancelledByAdmin]: {
      label: () => 'Cancelled by Admin',
      statusClass: '',
    },
    [CharterStatus.cancelledByOwner]: {
      label: () => 'Cancelled by Owner',
      statusClass: '',
    },
    [CharterStatus.cancelledByRenter]: {
      label: () => 'Cancelled By Renter',
      statusClass: '',
    },
    [CharterStatus.cancelled]: {
      label: () => 'Cancelled',
    },
  };
  // utility
  searchSubject = new Subject();
  debounceTimeMs = 800;
  isMobile: boolean = true;
  activeTabIndex = 0;
  subs: Subscription[] = [];
  dataLoading = true;
  count: number;
  limit = 10;
  showSearch: boolean = false;
  showFilter: boolean = false;

  constructor(
    public charterDAO: CharterDAO,
    private vesselDAO: VesselDAO,
    private payoutDAO: PayoutDAO,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute,
  ) {
    this.filteredCharters = this.createFilteredCharters();

    this.route.data.subscribe((data) => {
      this.ownerBoats.push(
        ...data.session.vesselResolve.map((boat) => ({
          label: boat.name,
          value: boat.id,
        })),
      );
    });

    this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
  }

  ngOnInit() {
    const searchSub = this.searchSubject
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe(() => {
        this.changeBoatFilter();
      });
    this.subs.push(searchSub);

    if (
      this.route.snapshot.queryParams &&
      this.route.snapshot.queryParams.activeTab
    ) {
      const tabs = this.filteredCharters.map((x) => x.value);
      if (
        tabs.some((tabNames) =>
          tabNames.includes(this.route.snapshot.queryParams.activeTab),
        )
      ) {
        this.activeTabIndex = tabs.findIndex((tabNames) => {
          return tabNames.includes(this.route.snapshot.queryParams.activeTab);
        });
      }
    }

    this.initAsyncData();
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // definitions

  createListGridData() {
    return new BehaviorSubject<ListGridAsyncData>({
      count: 0,
      page: 0,
      rows: [],
      searchQuery: null,
      numberOfPages: null,
      sortBy: null,
      sortOrder: null,
    });
  }

  createFilteredCharters(): CharterSection[] {
    return [
      {
        label: 'Ready to approve',
        value: [CharterStatus.needsApproval],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
      {
        label: 'Pending',
        value: [CharterStatus.approvedPending, CharterStatus.pendingOffers],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
      {
        label: 'Upcoming',
        value: [CharterStatus.booked],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
      {
        label: 'Completed',
        value: [CharterStatus.completed],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
      {
        label: 'Cancelled',
        value: [
          CharterStatus.cancelled,
          CharterStatus.cancelledByAdmin,
          CharterStatus.cancelledByOwner,
          CharterStatus.cancelledByRenter,
        ],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
      {
        label: 'All',
        value: ['all'],
        rows: [],
        listGridAsyncData: this.createListGridData(),
        page: 0,
      },
    ];
  }

  createColumnDefs() {
    this.columnDefs = [
      {
        headerName: 'Status',
        field: 'status',
        width: 80,
        filter: 'text',
        cellClass: (val) =>
          `${
            this.charterStatus[getTypeofCharter(val.data)].statusClass
          } grid-text`,
        desktopOnly: true,
        comparator: () => 0,
        valueFormatter: (val) =>
          this.charterStatus[getTypeofCharter(val.data)].label(val.data),
      },
      {
        headerName: 'Boat Name',
        field: 'boatName',
        width: 100,
        filter: 'text',
        cellClass: 'grid-text',
        mobileClass: () => 'mobile-grid-header',
      },
      {
        headerName: 'Departure',
        field: 'departure',
        width: 140,
        cellClass: 'grid-text',
        comparator: (valA, valB, nodeA, nodeB) =>
          this.dateComparator(valA, valB, nodeA, nodeB),
        valueFormatter: (val) =>
          `${moment(val.data.date).format('MMM DD, YYYY')} · ${
            val.data.startTime
          }`,
      },
      {
        headerName: 'Duration',
        field: 'duration',
        width: 40,
        filter: 'text',
        cellClass: 'grid-text',
        desktopOnly: true,
        valueFormatter: (val) => this.formatValue('duration', val),
      },
      {
        headerName: 'Guests',
        field: 'numOfPassengers',
        width: 40,
        filter: 'text',
        cellClass: 'grid-text',
        desktopOnly: true,
        valueFormatter: (val) => this.formatValue('guests', val),
      },
      {
        headerName: '',
        field: '',
        width: 40,
        filter: 'text',
        cellClass: 'grid-text',
        hide: true,
        valueFormatter: (val) =>
          `${this.formatValue('duration', val)}, ${this.formatValue(
            'guests',
            val,
          )}`,
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 100,
        filter: 'text',
        mobileClass: (val) =>
          `${
            this.charterStatus[getTypeofCharter(val.data)].statusClass
          } grid-text`,
        hide: true,
        valueFormatter: (val) =>
          this.charterStatus[getTypeofCharter(val.data)].label(val.data),
      },
      {
        headerName: '',
        field: '',
        width: 120,
        filter: 'text',
        cellClass: 'grid-text',
        cellRendererFramework: ManageButtonComponent,
      },
    ];
  }

  // utility

  changeBoatFilter() {
    this.filteredCharters = this.filteredCharters.map((fc) => ({
      ...fc,
      page: 0,
    }));
    this.initAsyncData();
  }

  formatValue(type, val) {
    if (type === 'duration') {
      return `${val.data.duration} hours`;
    } else if (type === 'guests') {
      if (this.isMobile) {
        return val.data.numOfPassengers
          ? `${val.data.numOfPassengers} Guests`
          : '';
      } else {
        return val.data.numOfPassengers
          ? `${val.data.numOfPassengers} Guests`
          : '-';
      }
    }
  }

  changeTab(event) {
    this.activeTabIndex = event.index;
    this.initAsyncData();
    const url = window.location.href;
    const urlSplit = url.split('?');
    const tabValue = this.filteredCharters.find(
      (fc) => fc.label === event.tab.textLabel,
    )?.value[0];
    const obj = {
      Title: 'Tab Change',
      Url: urlSplit[0] + '?activeTab=' + tabValue,
    };
    history.pushState(obj, obj.Title, obj.Url);
  }

  pageChange($event: number) {
    this.filteredCharters[this.activeTabIndex].page = $event;
    this.initAsyncData();
  }

  onAction(event) {
    if (event.type === 'manage') {
      this.router.navigate(['manage', 'bookings-manager', event.data.id]);
    }
  }

  getMobileClass(column, charter) {
    return column.mobileClass
      ? column.mobileClass({ data: charter })
      : 'grid-text';
  }

  monthToComparableNumber(date: string, time) {
    if (date === undefined || date === null || date.length !== 10) {
      return null;
    }
    const yearNumber = Number.parseInt(date.substring(0, 4));
    const monthNumber = Number.parseInt(date.substring(5, 7));
    const dayNumber = Number.parseInt(date.substring(8, 10));
    const timeNumber = Number(moment(time, 'hh:mm A').format('H'));

    return (
      yearNumber * 10000 + monthNumber * 100 + dayNumber + timeNumber / 100
    );
  }

  dateComparator(date1, date2, nodeA, nodeB) {
    const date1Number = this.monthToComparableNumber(
      nodeA.data.date,
      nodeA.data.startTime,
    );
    const date2Number = this.monthToComparableNumber(
      nodeB.data.date,
      nodeB.data.startTime,
    );

    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    return date1Number - date2Number;
  }

  // data

  private initAsyncData(): void {
    const status = this.filteredCharters[this.activeTabIndex].value;
    const { sortOrder, sortBy } =
      this.filteredCharters[this.activeTabIndex].listGridAsyncData.value;
    const searchQueryJson = {
      customer: {
        keys: ['email', 'firstName', 'lastName'],
        search: this.searchFilter,
      },
      boat: {
        keys: ['id'],
        search: this.boatFilter,
        exact: true,
      },
    };
    const searchQuery = JSON.stringify(searchQueryJson);
    const sub = this.charterDAO
      .loadGridCharters(
        this.filteredCharters[this.activeTabIndex].page,
        status,
        searchQuery,
        sortBy,
        sortOrder,
        this.limit,
      )
      .subscribe((chartersAndCount) => {
        this.count = chartersAndCount.count;
        this.filteredCharters[this.activeTabIndex].rows = chartersAndCount.rows;
        this.filteredCharters[this.activeTabIndex].listGridAsyncData.next({
          rows: chartersAndCount.rows,
          count: chartersAndCount.count,
          page: this.filteredCharters[this.activeTabIndex].page,
          searchQuery: searchQuery,
          numberOfPages: Math.ceil(chartersAndCount.count / this.limit) - 1,
          sortOrder: sortOrder,
          sortBy: sortBy,
        });

        this.createColumnDefs();
        this.dataLoading = false;
      });
    this.subs.push(sub);
  }

  calculateDiscount(charter) {
    return (
      Number(charter.discounts?.reduce((acc, curr) => acc + curr.amount, 0)) ||
      0
    );
  }

  getCleaningPrice(charter) {
    return Number(
      charter.payouts.find((x) => x.name === 'Cleaning')?.basePay || 0,
    );
  }
}

export interface BookingCharter extends Charter {
  status?: string;
  departure?: string;
}
interface CharterSection {
  label: string;
  value: string[];
  rows: Charter[];
  listGridAsyncData: any;
  page: number;
}
